/**
 * Pack exports (plan §10 pack integrity): every export commits the working edits as a revision
 * first (`commitForExport`), is recorded by the service (`POST /api/projects/:id/exports`), and
 * only then downloads. CSV and print are refused while invalid or unconfirmed, and run the
 * independent check first (src/app/checks.ts): a failed check stops the export. JSON is allowed.
 */
import { nextTick } from 'vue';
import { commitForExport, packFilename, toCSV, toJSON } from '@/export';
import { api, ApiError } from './api';
import { announce, toast } from './announce';
import { app, ui, viewerRef } from './state';
import { changeLabel, exportBlocker } from './navigation';
import { flushSave } from './persistence';
import { runCheck } from './checks';
import { contractOf } from '@/families/contract';

export type ExportKindUi = 'csv' | 'json' | 'print';
const KIND_LABEL: Record<ExportKindUi, string> = { csv: 'Cut list CSV', json: 'Project JSON', print: 'Print / PDF' };

export function download(filename: string, text: string, mime: string): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

let busy = false;
export async function exportPack(kind: ExportKindUi): Promise<void> {
  const block = exportBlocker();
  if (block && kind !== 'json') { announce(`Not available: ${block}`); return; }
  if (busy) return;
  busy = true;
  try {
    const store = app.store;
    const at = new Date().toISOString();
    const res = commitForExport(store, kind, { at, label: changeLabel('Exported') });
    if (!res.ok) { announce(`Not available: ${res.reason}`); return; }
    if (res.saved) announce(`Saved your edits as ${res.saved.id} before exporting.`);
    const { record, filename } = res;
    let content: string | undefined;
    if (kind === 'csv') content = toCSV(store.build, { revisionId: record.revisionId, generatedAt: at, fields: store.fields });
    if (kind === 'json') content = toJSON(store, { revisionId: record.revisionId, generatedAt: at });
    await flushSave();
    const projectId = ui.projectId!;
    /** The design is still exactly the revision (and assembly) that was checked. */
    const unchanged = (assemblyHash: string) => ui.projectId === projectId && store.currentRevisionId === record.revisionId && !store.isDirty && contractOf(store.build).assemblyHash === assemblyHash;
    if (kind !== 'json') {
      // a pack goes to the maker only after the independent check passes (the service enforces it too)
      const receipt = await runCheck(projectId, record.revisionId);
      if (!receipt) return;
      if (receipt.status === 'failed') {
        toast(`The independent check found ${receipt.errors.length === 1 ? 'a problem' : `${receipt.errors.length} problems`} in ${record.revisionId}: ${receipt.errors[0]}. Nothing was exported.`, { tone: 'warn' });
        announce(`Not exported: the independent check found a problem in ${record.revisionId}.`);
        return;
      }
      if (!unchanged(receipt.assemblyHash)) {
        toast('The design changed while it was being checked, so nothing was exported. Export again to check the new version.', { tone: 'warn' });
        return;
      }
    }
    try {
      await api.postExport(projectId, {
        revisionId: record.revisionId, manufacturingHash: record.manufacturingHash, kind: kind === 'print' ? 'pdf' : kind, filename,
        ...(content !== undefined ? { content } : {}),
      });
    } catch (e) {
      toast(`${KIND_LABEL[kind]} was not recorded by the service: ${e instanceof ApiError ? e.message : 'no answer'}`, { tone: 'warn' });
      // a pack the service refused is not handed out; the project JSON is a backup and still downloads
      if (kind !== 'json') return;
    }
    if (kind === 'csv') {
      download(filename, content!, 'text/csv');
      announce(`Cut list CSV downloaded for ${record.revisionId}.`);
      toast(`Cut list CSV downloaded for ${record.revisionId}, with revision, hash and status rows.`);
    } else if (kind === 'json') {
      download(filename, content!, 'application/json');
      announce(`Project JSON downloaded for ${record.revisionId}.`);
      toast(block ? 'Project JSON downloaded (marked not ready: validation.ok is false).' : `Project JSON downloaded for ${record.revisionId}.`);
    } else {
      await preparePrint(record.revisionId, { withImages: true });
      // the pack shown is the live design: print it only if it is still the checked revision
      if (!unchanged(contractOf(store.build).assemblyHash) || store.currentRevisionId !== record.revisionId) { ui.print = null; return; }
      announce(`Print pack ready for ${record.revisionId}.`);
      window.print();
    }
  } finally {
    busy = false;
  }
}

/** Fill the A4 print pack (#printRoot) for a revision: the hero is a snapshot of the live model. */
export async function preparePrint(revisionId: string | null, { withImages = false }: { withImages?: boolean } = {}): Promise<void> {
  let hero: string | null = null;
  if (withImages) {
    try { hero = (await viewerRef.value?.snapshot({ quality: 'preview', view: 'three-quarter', width: 1000, height: 1100, background: '#ffffff' })) ?? null; } catch { hero = null; }
  }
  ui.print = { revisionId, dirty: app.store.isDirty, hero, at: new Date().toISOString() };
  await nextTick();
}

/** ⌘P prints the existing revision (committing first when the design is exportable). */
export function onBeforePrint(): void {
  if (ui.print) return;
  if (!exportBlocker()) {
    const res = commitForExport(app.store, 'print', { label: changeLabel('Exported') });
    if (res.ok) {
      // printing straight from the browser is recorded only once the independent check passes
      void flushSave()
        .then(() => runCheck(ui.projectId!, res.record.revisionId))
        .then((r) => (r?.status === 'passed' ? api.postExport(ui.projectId!, { revisionId: res.record.revisionId, manufacturingHash: res.record.manufacturingHash, kind: 'pdf', filename: res.filename }) : null))
        .catch(() => null);
      ui.print = { revisionId: res.record.revisionId, dirty: false, hero: null, at: new Date().toISOString() };
      return;
    }
  }
  ui.print = { revisionId: app.store.currentRevisionId, dirty: app.store.isDirty, hero: null, at: new Date().toISOString() };
}
export function onAfterPrint(): void { ui.print = null; }

/** Look · Save picture (plan §3 step 4): a browser snapshot of the live model, `<name>-rN-<hash8>.png`. */
export async function savePicture(): Promise<string | null> {
  const viewer = viewerRef.value;
  if (!viewer) return null;
  const store = app.store;
  let url: string;
  try {
    url = await viewer.snapshot({ quality: 'finished', width: 1600, height: 1200, backdrop: store.design.appearance?.staging === 'room' });
  } catch {
    toast('The picture could not be made. The model is unchanged.', { tone: 'warn' });
    return null;
  }
  const rev = store.isDirty || !store.currentRevisionId ? null : store.currentRevisionId;
  const filename = packFilename({ name: store.design.appearance?.name, revisionId: rev, manufacturingHash: store.build.manufacturingHash, ext: 'png' });
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  ui.picture = { filename, bytes: Math.round((url.length - url.indexOf(',') - 1) * 0.75) };
  announce(`Picture saved: ${filename}.`);
  toast(`Picture saved: ${filename}.`);
  return filename;
}
